import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../api/apiClient";

export default function BenefitPage() {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [category, setCategory] = useState("");
  const [enterprise, setEnterprise] = useState("");
  const [incentives, setIncentives] = useState([]);
  const [eligibleForBenefit, setEligibleForBenefit] = useState(true);
  const [eligibilityMessage, setEligibilityMessage] = useState("");

  const details = state?.details || {};


  // OLD investment
  const costs = state?.costs || {};

  // NEW / ADDITIONAL investment
  const newCosts = state?.newCosts || {};

  const unitType = details.unitType || "New";

  // Which investment should be used for incentives/calculation
  const activeCosts =
    unitType === "Existing" ? newCosts : costs;

  // OLD FCI
  const oldFCI =
    (parseFloat(costs.land) || 0) +
    (parseFloat(costs.building) || 0) +
    (parseFloat(costs.machinery) || 0) +
    (parseFloat(costs.other_assets) || 0) +
    (parseFloat(costs.contingency) || 0) +
    (parseFloat(costs.pre_op) || 0);

  // NEW / ADDITIONAL FCI
  const newFCI =
    (parseFloat(newCosts.land) || 0) +
    (parseFloat(newCosts.building) || 0) +
    (parseFloat(newCosts.machinery) || 0) +
    (parseFloat(newCosts.other_assets) || 0) +
    (parseFloat(newCosts.contingency) || 0) +
    (parseFloat(newCosts.pre_op) || 0);

  // FINAL FCI USED FOR BENEFIT
  const FCI =
    (parseFloat(activeCosts.land) || 0) +
    (parseFloat(activeCosts.building) || 0) +
    (parseFloat(activeCosts.machinery) || 0) +
    (parseFloat(activeCosts.other_assets) || 0) +
    (parseFloat(activeCosts.contingency) || 0) +
    (parseFloat(activeCosts.pre_op) || 0);

  const oldPM = parseFloat(costs.machinery) || 0;
  const newPM = parseFloat(newCosts.machinery) || 0;

  // Sector
  const finalSector =
    details.sector === "Based on Data"
      ? enterprise === "Ultra-Mega"
        ? "Ultra-Mega Project"
        : enterprise === "Mega"
          ? "Mega Project"
          : enterprise === "Large"
            ? "Large Enterprise"
            : enterprise === "Medium"
              ? "Medium Enterprise"
              : enterprise === "Small"
                ? "Small Enterprise"
                : enterprise === "Micro"
                  ? "Micro Enterprise"
                  : "-"
      : details.sector;

  // Fetch block category
  useEffect(() => {
    if (!details.block) return;

    API.get("/location/category", {
      params: { block: details.block },
    })
      .then((res) => setCategory(res.data.category || "-"))
      .catch(() => setCategory("-"));
  }, [details.block]);

  // Enterprise determination
  useEffect(() => {
    const P = parseFloat(activeCosts.machinery) || 0;
    const T = parseFloat(details.turnover) || 0;

    if (FCI >= 1500) setEnterprise("Ultra-Mega");
    else if (FCI >= 200) setEnterprise("Mega");
    else if (P > 125 || T > 500) setEnterprise("Large");
    else if ((P > 26 && P <= 125) || (T > 101 && T <= 500))
      setEnterprise("Medium");
    else if ((P > 2.6 && P <= 25) || (T > 11 && T <= 100))
      setEnterprise("Small");
    else if ((P > 0.1 && P <= 2.5) || (T > 0.1 && T <= 10))
      setEnterprise("Micro");
    else setEnterprise("-");
  }, [FCI, activeCosts.machinery, details.turnover]);

  // EXISTING UNIT ELIGIBILITY
  useEffect(() => {
    if (unitType !== "Existing") {
      setEligibleForBenefit(true);
      setEligibilityMessage("");
      return;
    }

    let eligible = true;
    let message = "";

    // Ultra Mega / Mega / Large
    if (["Ultra-Mega", "Mega", "Large"].includes(enterprise)) {

      // NOT ELIGIBLE if new investment is LESS than required
      if (newFCI < oldFCI * 0.5) {
        eligible = false;

        message =
          "Additional Fixed Capital Investment should be at least 50% of current FCI.";
      }
    }

    // Medium / Small / Micro
    if (["Medium", "Small", "Micro"].includes(enterprise)) {

      // NOT ELIGIBLE if new PM is LESS than required
      if (newPM < oldPM * 0.5) {
        eligible = false;

        message =
          "Additional Plant & Machinery should be at least 50% of current Plant & Machinery.";
      }
    }

    setEligibleForBenefit(eligible);
    setEligibilityMessage(message);

  }, [unitType, enterprise, oldFCI, newFCI, oldPM, newPM]);

  // FETCH INCENTIVES
  useEffect(() => {
    if (
      !enterprise ||
      !finalSector ||
      !category ||
      !eligibleForBenefit
    ) {
      setIncentives([]);
      return;
    }

    API.get("/incentives", {
      params: {
        sector: finalSector,
        enterprise,
        block: category,
      },
    })
      .then((res) => {
        setIncentives(res.data || []);
      })
      .catch(() => setIncentives([]));
  }, [enterprise, finalSector, category, eligibleForBenefit]);

  // SAVE DATA
  const lastSaved = useRef(null);
  const hasSaved = useRef(false);

  const buildPayload = () => ({
    unitType,

    land: activeCosts.land,
    building: activeCosts.building,
    pm: activeCosts.machinery,
    fixedAsset: activeCosts.other_assets,
    contingency: activeCosts.contingency,
    preOperation: activeCosts.pre_op,
    workCapital: activeCosts.working_capital,

    totalCapex: FCI,

    blockName: details.block,
    village: details.village,

    sector: finalSector,

    turnover: details.turnover,

    category,
    enterprise,
    fci: FCI,

    oldFCI,
    newFCI,

    eligibleForBenefit,
    eligibilityMessage,
  });

  useEffect(() => {
    if (!state) return;
    if (!enterprise || !finalSector || !category) return;

    const newPayload = buildPayload();

    if (JSON.stringify(lastSaved.current) === JSON.stringify(newPayload)) {
      return;
    }

    if (hasSaved.current) {
      return;
    }

    hasSaved.current = true;

    const token = localStorage.getItem("token");

    API.post("/save-benefit-data", newPayload, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.data.insertId) {
          localStorage.setItem("last_input_id", res.data.insertId);
        }

        lastSaved.current = newPayload;
      })
      .catch(() => {
        localStorage.removeItem("last_input_id");
      });

    // eslint-disable-next-line
  }, [enterprise, finalSector, category, FCI]);

  return (
    <div className="max-w-3xl mx-auto mt-10 bg-white shadow p-6 rounded">
      <h1 className="text-3xl font-bold text-green-700 mb-6">
        Benefit Sheet
      </h1>

      {/* SUMMARY */}
      <table className="w-full border mb-6">
        <tbody>
          <tr>
            <td className="border p-2">Unit Type</td>
            <td className="border p-2">{unitType}</td>
          </tr>

          <tr>
            <td className="border p-2">Block</td>
            <td className="border p-2">{details.block}</td>
          </tr>

          <tr>
            <td className="border p-2">Village</td>
            <td className="border p-2">{details.village}</td>
          </tr>

          <tr>
            <td className="border p-2">Category</td>
            <td className="border p-2">{category}</td>
          </tr>

          <tr>
            <td className="border p-2">Sector</td>
            <td className="border p-2">{finalSector}</td>
          </tr>

          <tr>
            <td className="border p-2">Enterprise</td>
            <td className="border p-2">{enterprise}</td>
          </tr>

          {unitType === "Existing" && (
            <>
              <tr>
                <td className="border p-2">Current FCI</td>
                <td className="border p-2">₹ {oldFCI}</td>
              </tr>

              <tr>
                <td className="border p-2">Additional FCI</td>
                <td className="border p-2">₹ {newFCI}</td>
              </tr>
            </>
          )}

          <tr>
            <td className="border p-2">
              Incentive Calculation FCI
            </td>

            <td className="border p-2">
              ₹ {FCI}
            </td>
          </tr>
        </tbody>
      </table>

      {/* ELIGIBILITY */}
      {unitType === "Existing" && (
        <div
          className={`mb-6 rounded-lg border p-4 text-sm ${eligibleForBenefit
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800"
            }`}
        >
          <p className="font-semibold mb-1">
            Existing Unit Eligibility
          </p>

          <p>
            {eligibleForBenefit
              ? "Eligible for benefit"
              : "Not eligible for benefit"}
          </p>

          {!eligibleForBenefit && (
            <p className="mt-1">{eligibilityMessage}</p>
          )}
        </div>
      )}

      {/* INCENTIVES */}
      <h2 className="text-xl font-bold text-blue-900 mb-3">
        Applicable Incentives
      </h2>

      <table className="w-full border text-left">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2">Incentive Type</th>
            <th className="border p-2">Details</th>
          </tr>
        </thead>

        <tbody>
          {incentives.length === 0 && (
            <tr>
              <td
                colSpan="2"
                className="border p-3 text-center text-gray-500"
              >
                No incentives available for this selection.
              </td>
            </tr>
          )}

          {incentives.map((inc, index) => (
            <tr key={index}>
              <td className="border p-2">
                {inc.incentive_type}
              </td>

              <td className="border p-2">
                {inc.details}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* BUTTONS */}
      <div className="mt-6 text-right">
        <button
          onClick={() =>
            navigate("/calculator", {
              state: {
                details,
                costs: activeCosts,
                incentives,
                eligibleForBenefit,
                selectedFCI: unitType === "Existing" ? newFCI : FCI,
              },
            })
          }
          className="bg-green-600 text-white m-3 p-3 rounded font-semibold"
        >
          Calculate →
        </button>

        <button
          onClick={() => navigate("/")}
          className="bg-red-600 text-white m-3 p-3 rounded font-semibold"
        >
          Done
        </button>
      </div>
    </div>
  );
}